import { formatValueForDisplay } from 'features/drilldown/components/ontology/utils'
import { DrilldownTableColumnDef } from 'features/drilldown/components/table/config/DrilldownTableSchema'
import {
  EnrichedOntologyFieldDefinition,
  extractValueFromEntry,
  OntologyContextType,
} from 'features/dynamicOntology'
import { GeckoEntry } from 'types'

/**
 * Pull the formatted string value of a single field from a single entry.
 * Returns `undefined` when the field is missing or empty so callers can
 * skip rather than render an empty cell.
 */
export function extractTableValueFromEntry(
  entry: GeckoEntry,
  field: EnrichedOntologyFieldDefinition
): string | undefined {
  const value = extractValueFromEntry(entry, field)
  return value ? formatValueForDisplay(value, field.type) : undefined
}

/**
 * Pull all formatted string values for a single column out of the row's
 * entry pool. Multi-value cells (e.g. multiple linked indications under a
 * unit) become multiple strings here; the UI renders each as its own tag,
 * the export joins them with a separator.
 */
export function extractColumnTextValues(
  entries: ReadonlyArray<GeckoEntry>,
  columnDef: DrilldownTableColumnDef,
  schemaFields: OntologyContextType['fields']
): ReadonlyArray<string> {
  const valueFieldDef = schemaFields[columnDef.field]
  if (!valueFieldDef) {
    return []
  }
  return entries
    .filter((e) => e.type === valueFieldDef.entryType)
    .map((e) => extractTableValueFromEntry(e, valueFieldDef))
    .filter((v): v is string => !!v)
}

const EXPORT_VALUE_SEPARATOR = ', '

/**
 * Build a single string suitable for an Excel/CSV cell from a row's
 * entries and a column definition. Multi-value columns are joined with
 * a comma so the export retains the same information density as the UI.
 */
export function extractColumnExportString(
  entries: ReadonlyArray<GeckoEntry>,
  columnDef: DrilldownTableColumnDef,
  schemaFields: OntologyContextType['fields']
): string {
  return extractColumnTextValues(entries, columnDef, schemaFields).join(
    EXPORT_VALUE_SEPARATOR
  )
}
